<template>
  <div class="room-wrap">
    <!-- Header -->
    <div class="header">
      <div class="top-row">
        <button class="icon-btn logout" @click="router.push('/')">⏻</button>
        <span class="room-status" :class="room?.status">{{ statusLabel }}</span>
        <!-- <button class="icon-btn settings">⚙️</button> -->
      </div>
      
      <div class="title-section">
        <h1 class="room-title">{{ room?.name || 'اتاق بازی' }}</h1>
        <p class="room-subtitle" v-if="room?.childName">خوش اومدی {{ room.childName }} کوچولو! 👋</p>
      </div>
    </div>

    <!-- Games Grid -->
    <div class="games-grid">
      <div 
        v-for="g in games" 
        :key="g.id" 
        class="game-card" 
        @click="onTile(g)"
        :class="{ 'disabled': !isGameActive(g) }"
      >
        <div class="game-icon-box" :style="{ background: g.bg }">
          <span class="game-icon">{{ g.icon }}</span>
        </div>
        <div class="game-info">
          <span class="game-title">{{ g.title }}</span>
          <span class="play-btn-text">بازی کن! 🎮</span>
        </div>
      </div>
    </div>

    <!-- Security Overlay -->
    <div v-if="isLocked" class="lock-overlay">
      <div class="lock-card">
        <div class="lock-icon">🔒</div>
        <h2>اتاق قفل است</h2>
        <p>برای ورود، رمز عبور را وارد کنید</p>
        
        <!-- PIN Input -->
        <div v-if="securityType === 'pin'" class="pin-pad">
          <div class="pin-display">
            <span v-for="i in 4" :key="i" class="dot" :class="{ filled: pinInput.length >= i }"></span>
          </div>
          <div class="numpad">
            <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n" @click="enterPin(n)">{{ n }}</button>
            <button class="zero" @click="enterPin(0)">0</button>
            <button class="del" @click="pinInput = pinInput.slice(0, -1)">⌫</button>
          </div>
        </div>

        <!-- Pattern Input (Simplified: Click Sequence) -->
        <div v-else-if="securityType === 'pattern'" class="pattern-pad">
          <p class="hint">الگو را لمس کنید</p>
          <div class="pattern-grid">
            <button 
              v-for="i in 9" 
              :key="i-1" 
              class="dot-btn" 
              :class="{ active: patternInput.includes(String(i-1)) }"
              @click="enterPattern(i-1)"
            ></button>
          </div>
          <div class="actions">
             <button class="reset" @click="patternInput = []">تلاش مجدد</button>
             <button class="submit" @click="submitPattern" :disabled="patternInput.length < 3">تایید</button>
          </div>
        </div>

        <div v-if="errorMsg" class="error">{{ errorMsg }}</div>
      </div>
    </div>

    <!-- Decorative Elements -->
    <div class="decoration cloud-1">☁️</div>
    <div class="decoration cloud-2">☁️</div>
    <div class="decoration star-1">⭐</div>
    <div class="decoration star-2">🌟</div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'

const route = useRoute()
const router = useRouter()
const slug = route.params.slug

const ROOM_PUBLIC_QUERY = gql`
  query RoomPublic($slug: String!) { 
    roomPublic(slug: $slug) { 
      slug name childName endAt status visits securityType 
    } 
  }
`

const VERIFY_SECURITY_MUTATION = gql`
  mutation Verify($data: VerifyRoomSecurityInput!) {
    verifyRoomSecurity(data: $data) {
      ok
      token
    }
  }
`

const { result } = useQuery(ROOM_PUBLIC_QUERY, { slug })
const { mutate: verifySecurity } = useMutation(VERIFY_SECURITY_MUTATION)

const room = computed(() => result.value?.roomPublic || null)
const statusLabel = computed(() => room.value?.status === 'expired' ? 'منقضی شده' : 'فعال')
const securityType = computed(() => room.value?.securityType || 'none')

const isLocked = ref(true) // Default locked until checked
const pinInput = ref('')
const patternInput = ref([])
const errorMsg = ref('')

// Check lock stats
watch(room, (r) => {
  if (!r) return
  if ((r.securityType || 'none') === 'none') {
    isLocked.value = false
    return
  }
  
  // Check session
  const token = sessionStorage.getItem(`room_unlock_${slug}`)
  if (token && token.split('.').length === 3) {
    isLocked.value = false
  } else {
    sessionStorage.removeItem(`room_unlock_${slug}`)
    isLocked.value = true
  }
}, { immediate: true })

const games = [
  { id: 1, title: 'پازل', icon: '🧩', bg: 'linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%)' },
  { id: 2, title: 'نقاشی', icon: '🎨', bg: 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)' },
  { id: 3, title: 'اشکال', icon: '🔺', bg: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)' },
  { id: 9, title: 'داستان', icon: '📖', bg: 'linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)' },
  { id: 6, title: 'حروف', icon: '🔤', bg: 'linear-gradient(135deg, #4ade80 0%, #16a34a 100%)' },
  { id: 4, title: 'موسیقی', icon: '🎻', bg: 'linear-gradient(135deg, #fb7185 0%, #e11d48 100%)' },
  { id: 5, title: 'ارتباط', icon: '🔗', bg: 'linear-gradient(135deg, #fdba74 0%, #ea580c 100%)' },
  { id: 7, title: 'حیوانات', icon: '🐶', bg: 'linear-gradient(135deg, #94a3b8 0%, #475569 100%)' },
  { id: 8, title: 'زمین', icon: '🌍', bg: 'linear-gradient(135deg, #67e8f9 0%, #0891b2 100%)' },
]

function isGameActive(g) {
  return ['پازل', 'نقاشی', 'اشکال', 'داستان', 'حروف', 'موسیقی', 'ارتباط'].includes(g.title)
}

function onTile(g) {
  if (isLocked.value) return // Should be covered
  if (g.title === 'نقاشی') router.push(`/room/${slug}/paint`)
  if (g.title === 'پازل') router.push(`/room/${slug}/puzzle`)
  if (g.title === 'اشکال') router.push(`/room/${slug}/shapes`)
  if (g.title === 'داستان') router.push(`/room/${slug}/story`)
  if (g.title === 'حروف') router.push(`/room/${slug}/letters`)
  if (g.title === 'موسیقی') router.push(`/room/${slug}/music`)
  if (g.title === 'ارتباط') router.push(`/room/${slug}/connection`)
}

async function verify(val) {
  errorMsg.value = ''
  try {
    const { data } = await verifySecurity({ data: { roomSlug: slug, securityValue: val } })
    if (data?.verifyRoomSecurity?.ok && data.verifyRoomSecurity.token) {
      sessionStorage.setItem(`room_unlock_${slug}`, data.verifyRoomSecurity.token)
      isLocked.value = false
    } else {
      errorMsg.value = 'رمز اشتباه است ⛔'
      pinInput.value = ''
      patternInput.value = []
    }
  } catch (e) {
    errorMsg.value = 'خطا در بررسی رمز'
  }
}

function enterPin(n) {
  if (pinInput.value.length < 4) {
    pinInput.value += n
    if (pinInput.value.length === 4) {
      setTimeout(() => verify(pinInput.value), 300)
    }
  }
}

function enterPattern(idx) {
  const s = String(idx)
  if (!patternInput.value.includes(s)) {
    patternInput.value.push(s)
  }
}

function submitPattern() {
  verify(patternInput.value.join('-'))
}

</script>

<style scoped>
.room-wrap {
  min-height: 100vh;
  padding: 24px;
  background: linear-gradient(135deg, #a5f3fc 0%, #c4b5fd 50%, #fda4af 100%);
  font-family: 'Vazirmatn', sans-serif;
  overflow-x: hidden;
  position: relative;
}

.header {
  max-width: 800px;
  margin: 0 auto 32px;
}

.top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.icon-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid #fff;
  background: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}
.icon-btn:hover {
  background: #fff;
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.icon-btn.logout { color: #ef4444; }
.icon-btn.settings { color: #6366f1; }

.room-status {
  padding: 8px 16px;
  background: #fff;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.room-status.expired { color: #ef4444; background: #fee2e2; }

.title-section {
  text-align: center;
}

.room-title {
  font-size: 36px;
  font-weight: 900;
  color: #fff;
  margin: 0 0 8px;
  text-shadow: 2px 2px 0px rgba(0,0,0,0.1);
  background: linear-gradient(135deg, #7c3aed 0%, #db2777 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

.room-subtitle {
  color: #5b21b6;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 20px;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
}

.game-card {
  background: rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 4px solid #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  backdrop-filter: blur(8px);
}

.game-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 16px 40px rgba(0,0,0,0.15);
  background: #fff;
  z-index: 20;
}

.game-card.disabled {
  opacity: 0.6;
  filter: grayscale(1);
  cursor: not-allowed;
}
.game-card.disabled:hover {
  transform: none;
}

.game-icon-box {
  width: 100px;
  height: 100px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 20px rgba(0,0,0,0.1);
  transition: transform 0.5s ease;
}

.game-card:hover .game-icon-box {
  transform: rotate(10deg) scale(1.1);
}

.game-icon {
  font-size: 48px;
  filter: drop-shadow(0 4px 8px rgba(0,0,0,0.2));
}

.game-info {
  text-align: center;
}

.game-title {
  display: block;
  font-size: 18px;
  font-weight: 800;
  color: #4b5563;
  margin-bottom: 4px;
}

.play-btn-text {
  font-size: 12px;
  color: #8b5cf6;
  font-weight: 700;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
  display: block;
}

.game-card:hover .play-btn-text {
  opacity: 1;
  transform: translateY(0);
}

/* Decorations */
.decoration {
  position: fixed;
  font-size: 60px;
  opacity: 0.5;
  pointer-events: none;
  z-index: 1;
}
.cloud-1 { top: 10%; left: 5%; animation: float 6s ease-in-out infinite; }
.cloud-2 { top: 20%; right: 5%; animation: float 7s ease-in-out infinite reverse; }
.star-1 { bottom: 15%; left: 10%; font-size: 40px; animation: spin 10s linear infinite; }
.star-2 { bottom: 25%; right: 15%; font-size: 30px; animation: pulse 3s ease-in-out infinite; }

/* Lock Overlay */
.lock-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(10px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lock-card {
  background: #fff;
  border-radius: 24px;
  padding: 32px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0,0,0,0.3);
}
.lock-icon { font-size: 48px; margin-bottom: 16px; }
.lock-card h2 { margin: 0 0 8px; color: #1e293b; }
.lock-card p { color: #64748b; margin-bottom: 24px; }

.pin-display {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
}
.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #f1f5f9;
}
.dot.filled { 
  background: #2563eb; 
  border-color: #2563eb;
  transform: scale(1.2);
}

.numpad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.numpad button {
  aspect-ratio: 1;
  border-radius: 50%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 20px;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
}
.numpad button:active { background: #e2e8f0; transform: scale(0.95); }
.numpad .zero { grid-column: 2; }
.numpad .del { background: #fee2e2; color: #ef4444; border-color: #fecaca; }

.pattern-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 200px;
  margin: 0 auto 24px;
}
.dot-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e2e8f0;
  border: none;
  margin: 0 auto;
  cursor: pointer;
  transition: all 0.2s;
}
.dot-btn.active {
  background: #2563eb;
  box-shadow: 0 0 0 4px rgba(37,99,235,0.3);
}

.actions { display: flex; gap: 10px; justify-content: center; }
.reset { background: #f1f5f9; color: #64748b; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer; }
.submit { background: #2563eb; color: #fff; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-weight: 700; }
.submit:disabled { opacity: 0.5; }

.error {
  color: #ef4444;
  margin-top: 16px;
  font-weight: 700;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 0.8; }
}

@media (max-width: 600px) {
  .room-title { font-size: 28px; }
  .games-grid { grid-template-columns: repeat(2, 1fr); }
  .game-icon-box { width: 80px; height: 80px; }
  .game-icon { font-size: 40px; }
}
</style>
